import React, {MutableRefObject, useState} from 'react';

import {Card, GroupContainer, Row, Block, Col, Space, Button, Icon, Title, NoData, Modal, Menu, MenuItem} from '@mc/ui-v4';

import {WaitForQueryData, actionApiCall} from '@mc/react-app-v4';
import {LayerSelectionType, LayerSelector} from '../../overhead/content/LayerSelector';
import {useLayersConfig} from '../../../hooks/useLayersConfig';
import {EnumTopicType, LayerSettingsApi, RightsApi, TopicVO, UserRightsVO} from '../../../api';
import {CACHE_KEY_TOPICS_MAP, useAtlasPristupnosti, useTopicsMap} from '../../../hooks/useTopicsMap';
import {QueryClient, useQuery, useQueryClient, UseQueryResult} from 'react-query';
import {OpravneniConfiguration} from './OpravneniConfiguration';
import {CACHE_KEY_TOPIC_OPRAVNENI, CACHE_KEY_TOPICS_OWNED} from '../../../queryKeys';
import {mcEdit, mcMenuKebab} from '@mc/react-ui-icons';
import {NewEditTopic} from './NewEditTopic';
import {LegendConfigModal} from './LegendConfigModal';
import {MapEditorActions} from '@mc/react-mapio';
import {ApiCallWaitingModal} from '../ApiCallWaitingModal';
import {useAuth} from '@mc/react-auth';
import {TopicOwnersConfiguration} from './TopicOwnersConfiguration';
import {isUserAdminOrMapioAdmin} from '../../../helpers/AuthRights';

type Props = {
    topicId: string;
    mapRef: MutableRefObject<MapEditorActions>;
};
export const TopicConfiguration: React.FC<Props> = ({topicId, mapRef}) => {
    const {data: auth} = useAuth();

    const queryClient: QueryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const layersQuery = useLayersConfig();
    const topicsMapQuery: UseQueryResult<TopicVO[]> = useTopicsMap();
    const atlasPristupnosti = useAtlasPristupnosti();
    const opravneniTopicQuery = useQuery<UserRightsVO[]>([CACHE_KEY_TOPIC_OPRAVNENI, topicId], async () => (await RightsApi.getAllTopicRights(topicId)).data, {
        enabled: !!topicId,
    });
    const topicsOwnedQuery = useQuery<string[]>([CACHE_KEY_TOPICS_OWNED], async () => (await RightsApi.getTopicsOwned()).data);

    const topic: TopicVO = topicsMapQuery.data.find((item: TopicVO) => item.id === topicId);
    const bgLayers = layersQuery.data ? Object.values(layersQuery.data)?.filter(layer => topic?.backgroundLayers?.includes(layer.id)) : [];
    const fgLayers = layersQuery.data ? Object.values(layersQuery.data)?.filter(layer => topic?.foregroundLayers?.includes(layer.id)) : [];

    if (!topic) {
        return <NoData>Nepodařilo se najít téma</NoData>;
    }

    const isAdminOrMapioAdmin = isUserAdminOrMapioAdmin(auth);
    const isTopicOwner = topicsOwnedQuery.data?.includes(topicId) ?? false;
    const canEditTopic = isAdminOrMapioAdmin || isTopicOwner;

    const handleSetLayer = async (t: LayerSelectionType, id: number) => {
        setIsLoading(true);
        try {
            const updatedTopicSettings = {
                id: topic.id,
                nazev: topic.label,
                defaultForegroundLayer: t === LayerSelectionType.FOREGROUND_LAYER ? id : topic.defaultForegroundLayer,
                defaultBackgroundLayer: t === LayerSelectionType.BACKGROUND_LAYER ? id : topic.defaultBackgroundLayer,
                temaItems: null,
            };

            await Modal.showWaiting(
                actionApiCall(async () => (await LayerSettingsApi.updateTopicSettings(topicId, updatedTopicSettings)).data),
                ApiCallWaitingModal,
            );

            await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
            mapRef.current.updateTopic(topicId);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <GroupContainer
            title={
                <Title
                    inverse
                    intent={'inherit'}
                    extraLeft
                    extra={
                        canEditTopic && (
                            <Space>
                                <Button
                                    style={'link'}
                                    title={'Přejmenovat téma'}
                                    onClick={() =>
                                        Modal.show(({onClose}) => (
                                            <NewEditTopic topicId={topicId} initData={{nazev: topic.label}} mapRef={mapRef} onClose={onClose} />
                                        ))
                                    }
                                    icon={<Icon icon={mcEdit} />}
                                />
                                {/* MCW-29581 – rozšířená legenda je součástí atlasu přístupnosti */}
                                {atlasPristupnosti && (
                                    <Button
                                        style={'link'}
                                        title={'Další nastavení tématu'}
                                        icon={<Icon icon={mcMenuKebab} />}
                                        popupProps={{position: 'bottom right'}}
                                        menu={
                                            <Menu>
                                                <MenuItem
                                                    text={'Rozšířená legenda'}
                                                    onClick={() => Modal.show(({onClose}) => <LegendConfigModal topic={topic} mapRef={mapRef} onClose={onClose} />)}
                                                />
                                            </Menu>
                                        }
                                    />
                                )}
                            </Space>
                        )
                    }
                >
                    {topic.label}
                </Title>
            }
        >
            <div className={'config-menu-overflow'}>
                <WaitForQueryData queries={[layersQuery, topicsMapQuery]}>
                    {() => {
                        return (
                            <Space vertical>
                                <Card mode={'view'} title={'Výchozí podklad'}>
                                    <Block>
                                        <Row>
                                            <Col>
                                                <LayerSelector
                                                    topic={topic}
                                                    serverPublic={false}
                                                    flat
                                                    isAllowedToEdit={isAdminOrMapioAdmin}
                                                    backgroundLayerId={topic.defaultBackgroundLayer}
                                                    foregroundLayerId={topic.defaultForegroundLayer}
                                                    backgroundLayers={bgLayers}
                                                    foregroundLayers={fgLayers}
                                                    disabled={isLoading}
                                                    onSetLayer={handleSetLayer}
                                                />
                                            </Col>
                                        </Row>
                                    </Block>
                                </Card>
                                <WaitForQueryData queries={[opravneniTopicQuery]}>
                                    {() => {
                                        let topicLayersIds = new Set();

                                        opravneniTopicQuery.data?.forEach(({layers}) => {
                                            layers.forEach(({layer}) => {
                                                topicLayersIds.add(layer.id);
                                            });
                                        });

                                        const topicLayersData = Object.values(layersQuery.data)?.filter(layer => topicLayersIds?.has(layer.id));
                                        return <OpravneniConfiguration opravneniItems={opravneniTopicQuery.data} topicLayers={topicLayersData} showLayers />;
                                    }}
                                </WaitForQueryData>
                                {topic.typ === EnumTopicType.USER_TOPIC && <TopicOwnersConfiguration topic={topic} />}
                            </Space>
                        );
                    }}
                </WaitForQueryData>
            </div>
        </GroupContainer>
    );
};
